import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';

import type { StorySlide } from '@sorare/core/src/components/Story';
import { Story } from '@sorare/core/src/components/Story';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { isType } from '@sorare/core/src/lib/gql';

import { RewardUnlockedStory } from 'components/missions/stories/RewardUnlockedStory';
import { useClaimTask } from 'components/missions/useClaimTask';

import type { TaskTrckClaimStory_decisivePlayerPickerTask } from './__generated__/index.graphql';
import { ChooseYourRewardStory } from '../DailyTask/DecisivePlayerPickerTaskStatus/ChooseYourRewardStory';
import { QuickplayNextLevelStory } from '../stories/QuickplayNextLevelStory';

type Props = {
  task: TaskTrckClaimStory_decisivePlayerPickerTask;
  onClose: () => void;
};
export const TaskTrckClaimStory = ({ task, onClose }: Props) => {
  const [claimTask] = useClaimTask();
  const { sport } = useSportContext();
  const [signedAlternativeRewardConfigs, setSignedAlternativeRewardConfigs] =
    useState<string | undefined>();

  const { isMobileApp, postMessage } = useIsMobileApp();

  if (!isType(task, 'DecisivePlayerPickerTask')) {
    return null;
  }

  const rewardConfigs = task.rewardConfigs || [];
  const alternativeRewardConfigs = task.alternativeRewardConfigs || [];
  const canChooseAlternativeRewardConfigs = alternativeRewardConfigs.length > 0;

  return (
    <Story
      goBackToBeginning={false}
      onClose={() => {
        if (isMobileApp) {
          postMessage('taskClaimed', { taskId: task.id });
        }
        onClose();
      }}
      stories={[
        ...(canChooseAlternativeRewardConfigs
          ? [
              {
                duration: 2,
                withBackChevron: false,
                withNextChevron: false,
                autoPlayNext: false,
                bodyElement: ({ onNext }) => (
                  <ChooseYourRewardStory
                    key="choose-reward-story"
                    rewardConfigs={rewardConfigs}
                    alternativeRewardConfigs={alternativeRewardConfigs}
                    setSignedAlternativeRewardConfigs={
                      setSignedAlternativeRewardConfigs
                    }
                    onConfirm={async () => {
                      await claimTask({
                        variables: {
                          taskId: task.id,
                          signedAlternativeRewardConfigs,
                          sport,
                        },
                      });
                      onNext();
                    }}
                  />
                ),
              } as StorySlide,
            ]
          : []),
        {
          duration: 2,
          withBackChevron: false,
          withNextChevron: false,
          autoPlayNext: false,
          bodyElement: ({ onNext }) => (
            <RewardUnlockedStory
              key="reward-unlocked-story"
              open
              onClose={onNext}
              task={task}
              alternativeRewardConfigs={
                signedAlternativeRewardConfigs
                  ? alternativeRewardConfigs[0].rewardConfigs
                  : undefined
              }
            />
          ),
        },
        {
          duration: 2,
          withBackChevron: false,
          withNextChevron: false,
          autoPlayNext: false,
          bodyElement: ({ onNext }) => (
            <QuickplayNextLevelStory
              key="quickplay-next-level-story"
              onClose={onNext}
              task={task}
            />
          ),
        },
      ]}
    />
  );
};

TaskTrckClaimStory.fragments = {
  taskInterface: gql`
    fragment TaskTrckClaimStory_decisivePlayerPickerTask on DecisivePlayerPickerTask {
      id
      mode
      rewardConfigs {
        id
        ...ChooseYourRewardStory_rewardConfig
      }
      alternativeRewardConfigs {
        ...ChooseYourRewardStory_alternativeRewardConfig
      }
      ...RewardUnlockedStory_taskInterface
      ...QuickplayNextLevelStory_taskInterface
    }
    ${ChooseYourRewardStory.fragments.rewardConfig}
    ${ChooseYourRewardStory.fragments.alternativeRewardConfig}
    ${RewardUnlockedStory.fragments.taskInterface}
    ${QuickplayNextLevelStory.fragments.taskInterface}
  ` as TypedDocumentNode<TaskTrckClaimStory_decisivePlayerPickerTask>,
};
